import * as THREE from 'three';
import { SceneModule, SceneContext, EnvironmentState } from '../types';
import { CameraView } from '../../engine/cameraRig';
import { WeatherType } from '../../app/store';
import { StylizedWater } from '../shared/water';
import { StylizedCloudField } from '../shared/clouds';
import { InstancedPineForest, TreeInstanceData } from '../shared/vegetation';
import { PrayerFlagLine, CableCarSystem } from '../shared/props';
import { AlpineYaks } from '../shared/yaks';
import { disposeHierarchy } from '../../engine/disposal';
import { t } from '../../ui/i18n';
import { store } from '../../app/store';

export class YulongScene implements SceneModule {
  public id: string = 'yulong';
  public nameKey: string = 'scene_yulong';

  public root: THREE.Group = new THREE.Group();
  private terrainMesh!: THREE.Mesh;
  private lakeWater!: StylizedWater;
  private clouds!: StylizedCloudField;
  private pineForest!: InstancedPineForest;
  private prayerFlags!: PrayerFlagLine;
  private cableCar!: CableCarSystem;
  private yaks!: AlpineYaks;

  private snowMaterial!: THREE.MeshStandardMaterial;
  private rockMaterial!: THREE.MeshStandardMaterial;
  private meadowMaterial!: THREE.MeshStandardMaterial;
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private mouseVec: THREE.Vector2 = new THREE.Vector2();
  private showToastFn?: (msg: string) => void;

  public cameras: CameraView[] = [
    {
      id: 1,
      nameKey: 'view_yulong_1',
      position: new THREE.Vector3(0, 18, 48),
      target: new THREE.Vector3(0, 6, 0),
      mode: 'orbit',
      minDistance: 12,
      maxDistance: 120,
    },
    {
      id: 2,
      nameKey: 'view_yulong_2',
      position: new THREE.Vector3(-14, 18, 14),
      target: new THREE.Vector3(-8, 22, -10),
      mode: 'orbit',
      minDistance: 6,
      maxDistance: 60,
    },
    {
      id: 3,
      nameKey: 'view_yulong_3',
      position: new THREE.Vector3(14, 4, 18),
      target: new THREE.Vector3(0, 1, 6),
      mode: 'orbit',
      minDistance: 4,
      maxDistance: 45,
    },
    {
      id: 4,
      nameKey: 'view_yulong_4',
      position: new THREE.Vector3(0, 42, -5),
      target: new THREE.Vector3(0, 36, -30),
      mode: 'orbit',
      minDistance: 10,
      maxDistance: 100,
    },
    {
      id: 5,
      nameKey: 'view_yulong_5',
      position: new THREE.Vector3(6, 3, 24),
      target: new THREE.Vector3(0, 2, 20),
      mode: 'orbit',
      minDistance: 2,
      maxDistance: 35,
    },
    {
      id: 6,
      nameKey: 'view_yulong_6',
      position: new THREE.Vector3(0, 15, 30),
      target: new THREE.Vector3(0, 12, 0),
      mode: 'fly',
    },
  ];

  public async load(ctx: SceneContext, onProgress: (p: number) => void): Promise<void> {
    this.showToastFn = ctx.showToast;
    this.root.name = 'YulongSnowMountainScene';

    onProgress(0.1);
    this.createMaterials();

    onProgress(0.25);
    this.createMountainTerrain();

    onProgress(0.45);
    // Blue Moon Valley turquoise lake
    this.lakeWater = new StylizedWater({
      width: 42,
      height: 24,
      deepColor: 0x0284c7, // deep emerald cyan
      shallowColor: 0x38bdf8, // light turquoise
    });
    this.lakeWater.mesh.position.set(0, 0.4, 8);
    this.root.add(this.lakeWater.mesh);

    // Lake travertine terrace borders
    this.createLakeTerraces();

    onProgress(0.6);
    // Pine forest on meadow and lower slopes
    this.createForest();

    onProgress(0.75);
    // Tibetan / Naxi Prayer flags
    this.prayerFlags = new PrayerFlagLine(
      new THREE.Vector3(-14, 1.2, 18),
      new THREE.Vector3(-4, 2.0, 22),
      15
    );
    this.root.add(this.prayerFlags.root);

    // Cable car line ascending toward mountain ridge
    this.cableCar = new CableCarSystem(
      new THREE.Vector3(-25, 2.0, 15),
      new THREE.Vector3(-8, 34.0, -28)
    );
    this.root.add(this.cableCar.root);

    onProgress(0.85);
    // Alpine Yaks in the meadow
    this.yaks = new AlpineYaks([
      new THREE.Vector3(-3, 0.8, 22),
      new THREE.Vector3(5, 0.8, 20),
      new THREE.Vector3(8, 0.9, 25),
      new THREE.Vector3(-8, 1.1, 26),
    ]);
    this.root.add(this.yaks.root);

    // Drifting clouds across peaks
    this.clouds = new StylizedCloudField(7, 30);
    this.root.add(this.clouds.root);

    // Setup interactive raycasting clicks on canvas
    this.setupInteractivity(ctx);

    ctx.sceneRoot.add(this.root);
    onProgress(1.0);
  }

  private createMaterials(): void {
    this.snowMaterial = new THREE.MeshStandardMaterial({
      color: 0xf8fafc,
      roughness: 0.6,
      metalness: 0.1,
      flatShading: true,
    });

    this.rockMaterial = new THREE.MeshStandardMaterial({
      color: 0x475569, // slate cliff rock
      roughness: 0.85,
      metalness: 0.1,
      flatShading: true,
    });

    this.meadowMaterial = new THREE.MeshStandardMaterial({
      color: 0x2e6b3e, // rich alpine green
      roughness: 0.9,
      flatShading: true,
    });
  }

  private createMountainTerrain(): void {
    // 13 Jagged Snow Ridge Peaks
    const mountainGroup = new THREE.Group();

    // Main massif base
    const baseGeo = new THREE.BoxGeometry(90, 8, 60);
    baseGeo.translate(0, -4, -10);
    const baseMesh = new THREE.Mesh(baseGeo, this.meadowMaterial);
    baseMesh.receiveShadow = true;
    mountainGroup.add(baseMesh);

    // 13 iconic jagged peak silhouettes
    const peakConfigs = [
      { x: 0, z: -28, h: 42, r: 12 },    // Main Summit: Shanzidou (5596m)
      { x: -9, z: -25, h: 36, r: 10 },
      { x: 8, z: -26, h: 37, r: 10 },
      { x: -17, z: -22, h: 30, r: 8 },
      { x: 16, z: -23, h: 31, r: 9 },
      { x: -24, z: -18, h: 26, r: 7 },
      { x: 23, z: -19, h: 27, r: 8 },
      { x: -30, z: -15, h: 22, r: 7 },
      { x: 29, z: -16, h: 23, r: 7 },
      { x: -5, z: -18, h: 24, r: 8 },   // Secondary glacier ridge
      { x: 5, z: -19, h: 25, r: 8 },
      { x: -12, z: -14, h: 19, r: 6 },
      { x: 12, z: -15, h: 20, r: 6 },
    ];

    for (let i = 0; i < peakConfigs.length; i++) {
      const cfg = peakConfigs[i];
      // Rock lower body
      const rockGeo = new THREE.ConeGeometry(cfg.r, cfg.h, 6);
      rockGeo.translate(0, cfg.h / 2, 0);
      const rockMesh = new THREE.Mesh(rockGeo, this.rockMaterial);
      rockMesh.position.set(cfg.x, 0, cfg.z);
      rockMesh.castShadow = true;
      rockMesh.receiveShadow = true;
      mountainGroup.add(rockMesh);

      // Snow cap covering upper 55%
      const snowGeo = new THREE.ConeGeometry(cfg.r * 0.58, cfg.h * 0.55, 6);
      snowGeo.translate(0, cfg.h * 0.72, 0);
      const snowMesh = new THREE.Mesh(snowGeo, this.snowMaterial);
      snowMesh.position.set(cfg.x, 0, cfg.z);
      snowMesh.castShadow = true;
      snowMesh.receiveShadow = true;
      mountainGroup.add(snowMesh);
    }

    // Alpine meadow rolling ground in front
    const meadowGeo = new THREE.CylinderGeometry(44, 46, 3, 24);
    meadowGeo.translate(0, -1.5, 18);
    this.terrainMesh = new THREE.Mesh(meadowGeo, this.meadowMaterial);
    this.terrainMesh.receiveShadow = true;
    mountainGroup.add(this.terrainMesh);

    this.root.add(mountainGroup);
  }

  private createLakeTerraces(): void {
    // Stepped white travertine terraces (signature of Blue Moon Valley)
    const terraceMat = new THREE.MeshStandardMaterial({
      color: 0xe2e8f0,
      roughness: 0.7,
      flatShading: true,
    });

    const borders = [
      new THREE.Vector3(-18, 0.4, 0),
      new THREE.Vector3(0, 0.35, -2),
      new THREE.Vector3(18, 0.4, 0),
      new THREE.Vector3(-12, 0.3, 18),
      new THREE.Vector3(12, 0.3, 18),
    ];

    for (const b of borders) {
      const geo = new THREE.CylinderGeometry(2.5, 3.2, 0.8, 7);
      const mesh = new THREE.Mesh(geo, terraceMat);
      mesh.position.copy(b);
      mesh.receiveShadow = true;
      this.root.add(mesh);
    }

    // Wooden viewing deck platform
    const deckGeo = new THREE.BoxGeometry(6, 0.4, 4);
    const deckMat = new THREE.MeshStandardMaterial({ color: 0x78350f, roughness: 0.85 });
    const deck = new THREE.Mesh(deckGeo, deckMat);
    deck.position.set(16, 0.8, 16);
    deck.receiveShadow = true;
    this.root.add(deck);
  }

  private createForest(): void {
    const treeInstances: TreeInstanceData[] = [];

    // Dense clusters around lake margins and lower mountain flanks
    for (let i = 0; i < 75; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 18 + Math.random() * 22;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * (radius * 0.7) + 8;

      // Avoid placing trees inside lake basin
      if (Math.abs(x) < 16 && z > -2 && z < 18) continue;

      treeInstances.push({
        position: new THREE.Vector3(x, 0.2 + (z < 0 ? -z * 0.25 : 0), z),
        scale: 0.7 + Math.random() * 0.6,
        rotationY: Math.random() * Math.PI * 2,
      });
    }

    this.pineForest = new InstancedPineForest(treeInstances);
    this.root.add(this.pineForest.mesh);
  }

  private setupInteractivity(ctx: SceneContext): void {
    const canvas = ctx.renderer.domElement;

    const onPointerDown = (e: PointerEvent) => {
      // Calculate normalized mouse coords
      const rect = canvas.getBoundingClientRect();
      this.mouseVec.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouseVec.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(this.mouseVec, ctx.camera);

      // Check cloud click (Toy 1)
      if (this.clouds.partNearestCloud(this.raycaster)) {
        this.showToastFn?.(t('toy_cloud_parted', store.getState().language));
        return;
      }

      // Check prayer flag click (Toy 2)
      const flagHits = this.raycaster.intersectObjects(this.prayerFlags.root.children, true);
      if (flagHits.length > 0) {
        this.prayerFlags.triggerRipple();
        this.showToastFn?.(t('toy_flags_rippled', store.getState().language));
        return;
      }

      // Check yak click (Toy 3)
      const yakHits = this.raycaster.intersectObjects(this.yaks.root.children, true);
      if (yakHits.length > 0) {
        this.yaks.feedYak(0);
        this.showToastFn?.(t('toy_yak_fed', store.getState().language));
      }
    };

    if (canvas && typeof canvas.addEventListener === 'function') {
      canvas.addEventListener('pointerdown', onPointerDown);
    }
  }

  public update(dt: number, env: EnvironmentState): void {
    const time = performance.now() / 1000;

    // Alpenglow tint on snow during dawn & dusk
    if (this.snowMaterial) {
      if (env.timeOfDay === 0) {
        // Dawn: warm pink alpenglow
        this.snowMaterial.color.setHex(0xfecdd3);
      } else if (env.timeOfDay === 3 || env.timeOfDay === 4) {
        // Golden/dusk: amber sunset glow
        this.snowMaterial.color.setHex(0xfde68a);
      } else if (env.timeOfDay === 5) {
        // Night: cool moonlit blue
        this.snowMaterial.color.setHex(0xcfd8dc);
      } else {
        // Bright day
        this.snowMaterial.color.setHex(0xf8fafc);
      }
    }

    this.lakeWater.update(dt);
    this.clouds.update(dt);
    this.prayerFlags.update(dt, time);
    this.cableCar.update(dt);
    this.yaks.update(dt, time);
  }

  public setTimeOfDay(_t: number): void {}
  public setWeather(_w: WeatherType): void {}

  public getSuggestedTracks(): string[] {
    return ['kmac-mountain-breeze', 'guzheng-snow-peak'];
  }

  public onToyAction(toyId: string): void {
    if (toyId === 'cloud') {
      if (this.clouds.clouds[0]) {
        this.clouds.clouds[0].isParting = true;
        this.showToastFn?.(t('toy_cloud_parted', store.getState().language));
      }
    } else if (toyId === 'flags') {
      this.prayerFlags.triggerRipple();
      this.showToastFn?.(t('toy_flags_rippled', store.getState().language));
    } else if (toyId === 'yak') {
      this.yaks.feedYak(0);
      this.showToastFn?.(t('toy_yak_fed', store.getState().language));
    }
  }

  public dispose(): void {
    disposeHierarchy(this.root);
    this.lakeWater.dispose();
    this.clouds.dispose();
    this.pineForest.dispose();
    this.yaks.dispose();
  }
}
